




























import {
  PropType,
  defineComponent,
  watch,
  onBeforeUnmount,
  onMounted,
  computed,
  ref,
  useContext
} from '@nuxtjs/composition-api';
import { SveaConfig, useSveaOrder, EstimatedShippingMethod } from '@wemade-vsf/payment-svea';
import { SfHeading } from '@storefront-ui/vue';
import RadioBox from 'components/theme/Form/RadioBox.vue';
import SkeletonLoader from 'components/theme/SkeletonLoader.vue';
import type { SveaOrder, SetShippingMethodsOnCartInput } from '@wemade-vsf/magento-api';

declare global {
  interface Window {
    scoApi: {
      observeEvent(identifier: string, callback: any)
    }
  }
}

export default defineComponent({
  name: 'ShippingMethods',
  props: {
    sveaConfig: {
      type: Object as PropType<SveaConfig | null | undefined>
    },
    order: {
      type: Object as PropType<SveaOrder | null | undefined>
    },
    sveaReady: {
      type: Boolean,
      default: false
    },
    cartId: {
      type: String,
      required: true
    },
    includeTax: {
      type: Boolean,
      default: false
    },
    selectedShippingMethod: {
      type: String,
      default: ''
    },
    defaultShippingMethod: {
      type: String
    }
  },
  components: {
    SfHeading,
    RadioBox,
    SkeletonLoader
  },
  setup (props, { emit }) {
    const { getEstimatedShippingMethods } = useSveaOrder();

    const shippingAddress = computed(() => props.order ? (props.order as SveaOrder).ShippingAddress : null);
    const estimatedShippingMethods = ref<EstimatedShippingMethod[]>([])
    const selectedShippingMethod = ref<EstimatedShippingMethod | null>(null);
    const selectedShippingMethodCode = ref<string>('');

    function setSelectedMethod(methodCode: string) {
      const method = estimatedShippingMethods.value.find(m => m.method_code === methodCode);
      if (method) {
        selectedShippingMethod.value = method;
        selectedShippingMethodCode.value = selectedShippingMethod.value.method_code;
      } else {
        setDefaultMethod();
      }
    }

    function setDefaultMethod() {
      if (!estimatedShippingMethods.value.length) return;
      if (props.selectedShippingMethod) {
        const method = estimatedShippingMethods.value.find(m => m.method_code === props.selectedShippingMethod)
        if (method) {
          setSelectedMethod(method.method_code)
          return
        }
      } else if (props.defaultShippingMethod) {
        const method = estimatedShippingMethods.value.find(m => (m.carrier_code + '_' + m.method_code) == props.defaultShippingMethod);
        if (method) {
          setSelectedMethod(method.method_code);
          save();
          return;
        }
      }

      /* set preselect method */
      const sortedMethods = estimatedShippingMethods.value.sort((a, b) => (a.amount ?? 0) - (b.amount ?? 0))
      setSelectedMethod(sortedMethods[0].method_code);
      save();
    }

    const { app } = useContext();

    const getShippingAmount = (method: EstimatedShippingMethod) => {
      if (!method || !method.price_excl_tax) return null
      return `${app.$fc(method.price_incl_tax)} <small>(${app.$fc(method.price_excl_tax)} ${app.i18n.t('excl. VAT')})</small>`
    }

    async function save() {
      setSelectedMethod(selectedShippingMethodCode.value);
      if (!selectedShippingMethod.value) return;
      const params: SetShippingMethodsOnCartInput = {
        cart_id: props.cartId,
        shipping_methods: [
          {
            carrier_code: selectedShippingMethod.value.carrier_code,
            method_code: selectedShippingMethod.value.method_code
          }
        ]
      }
      try {
        await app.$vsf.$magento.api.setShippingMethodsOnCart(params);
      } catch (err) {
        // we just ignore this as we know this will throw
      } finally {
        emit('change:method', selectedShippingMethod.value);
      }
    }

    let postcodeListener = null;

    function initListeners() {
      if (!window) return;
      postcodeListener = window.scoApi.observeEvent('identity.postalCode', (data) => {
        if (data.value) {
          updateShipping(data.value)
        }
      })
    }

    async function updateShipping(postcode: string = '') {
      if (postcode === '99999') return;
      let country = props.sveaConfig?.default_country ?? 'NO'
      postcode = postcode ? postcode : props.sveaConfig?.default_postcode ?? ''
      if (shippingAddress.value?.CountryCode) {
        country = shippingAddress.value.CountryCode;
      }
      if (shippingAddress.value?.PostalCode) {
        postcode = shippingAddress.value.PostalCode
      }
      estimatedShippingMethods.value = await getEstimatedShippingMethods(props.cartId, { country_id: country, postcode });
      setDefaultMethod();
    }

    watch(() => props.sveaReady, (newVal) => {
      if (newVal) {
        initListeners()
      }
    }, { immediate: true })

    onMounted(async() => {
      await updateShipping();
    })

    onBeforeUnmount(() => {
      //@ts-ignore
      postcodeListener ? postcodeListener() : null;
    });

    return {
      estimatedShippingMethods,
      selectedShippingMethodCode,
      getShippingAmount,
      save
    }
  }
})
